import React, { useState, useEffect, useRef } from 'react';
import { UploadCloud, ImageIcon, Save, Lock } from 'lucide-react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  useUpdateCourseDetailsMutation,
  useUploadCourseCoverMutation,
  useGetAllCategoriesQuery,
} from '../../../api/apiSlice';
import Loading from '../../common/Loading';
import { computePricing, formatKzt } from '../../../lib/coursePricing';

// Theme-aware react-select styling using the classNames API (v5.5+).
// Each callback returns a string of Tailwind classes; light & dark are both styled.
const selectClasses = {
  control: ({ isFocused }) =>
    `!bg-white dark:!bg-slate-900 !border ${
      isFocused
        ? '!border-indigo-500 !ring-2 !ring-indigo-500/20'
        : '!border-slate-300 dark:!border-slate-700'
    } !rounded-xl !min-h-[42px] !shadow-none`,
  valueContainer: () => '!px-3',
  input: () => '!text-slate-900 dark:!text-white',
  placeholder: () => '!text-slate-400 dark:!text-slate-500',
  singleValue: () => '!text-slate-900 dark:!text-white',
  multiValue: () =>
    '!bg-indigo-100 dark:!bg-indigo-500/20 !rounded-md !overflow-hidden',
  multiValueLabel: () => '!text-indigo-700 dark:!text-indigo-300 !font-medium !px-2 !py-0.5',
  multiValueRemove: () =>
    '!text-indigo-700 dark:!text-indigo-300 hover:!bg-indigo-200 dark:hover:!bg-indigo-500/30 hover:!text-indigo-900',
  menu: () =>
    '!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-lg !mt-1 !overflow-hidden !z-30',
  menuList: () => '!p-1',
  option: ({ isFocused, isSelected }) =>
    `!rounded-lg !cursor-pointer !px-3 !py-2 !text-sm ${
      isSelected
        ? '!bg-indigo-600 !text-white'
        : isFocused
          ? '!bg-slate-100 dark:!bg-slate-800 !text-slate-900 dark:!text-white'
          : '!bg-transparent !text-slate-700 dark:!text-slate-200'
    }`,
  noOptionsMessage: () => '!text-slate-400 dark:!text-slate-500 !py-2',
  indicatorSeparator: () => '!bg-slate-200 dark:!bg-slate-700',
  dropdownIndicator: () => '!text-slate-400 hover:!text-slate-600 dark:hover:!text-slate-300',
  clearIndicator: () => '!text-slate-400 hover:!text-slate-600 dark:hover:!text-slate-300',
};

// isLocked = true когда курс active/pending_review/archived.
// В этом режиме мы:
//  • title делаем read-only (нельзя bait-and-switch название)
//  • cover-загрузка отключена (визуальная подмена продукта)
//  • остальные поля (description / price / discount / category) — редактируемы,
//    но при сабмите title даже не шлём (бэк всё равно вернёт 403 если придёт).
const CourseDetailsForm = ({ course, isLocked = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: 0,
    discount: 0,
    category: [],
  });
  const [coverProgress, setCoverProgress] = useState(0);

  const [updateDetails, { isLoading: isUpdating }] = useUpdateCourseDetailsMutation();
  const [uploadCover, { isLoading: isUploading }] = useUploadCourseCoverMutation();
  const { data: categories = [], isLoading: isLoadingCategories } = useGetAllCategoriesQuery();

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!course) return;
    setFormData({
      title: course.title || '',
      description: course.description || '',
      price: course.price ?? 0,
      discount: course.discount ?? 0,
      category: Array.isArray(course.category)
        ? course.category.map((cat) => (typeof cat === 'object' && cat !== null ? cat._id : cat))
        : [],
    });
    // Зависимость по id курса, а не по объекту. Иначе после uploadCourseCover
    // RTK Query инвалидирует course → useEffect фаерится → перезаписывает
    // formData → пользователь теряет всё, что набрал но не сохранил.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?._id || course?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'price' || name === 'discount' ? Number(value) : value,
    }));
  };

  const handleCategoryChange = (selectedOptions) => {
    const categoryIds = (selectedOptions || []).map((option) => option.value);
    setFormData((prev) => ({ ...prev, category: categoryIds }));
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setCoverProgress(0);
    try {
      await uploadCover({
        courseId: course._id,
        coverFile: file,
        onProgress: setCoverProgress,
      }).unwrap();
      setCoverProgress(100);
      toast.success(t('common.save'));
      setTimeout(() => setCoverProgress(0), 600);
    } catch (error) {
      toast.error(error.data?.message || t('editor.uploadError'));
      setCoverProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error(t('editor.courseTitle'));
      return;
    }
    // discountPrice ВСЕГДА вычисляем от price + discount%. Никакого ручного
    // ввода — это исключает рассинхрон (раньше каталог показывал старый
    // discountPrice, а в форме был свежий discount%).
    const price = Number(formData.price) || 0;
    const discount = Math.max(0, Math.min(100, Number(formData.discount) || 0));
    const computedDiscountPrice =
      discount > 0 ? Math.round(price * (1 - discount / 100)) : null;

    try {
      const base = {
        description: formData.description,
        price,
        discount,
        discountPrice: computedDiscountPrice,
        category: formData.category,
      };
      // В locked не шлём title.
      const payload = isLocked ? base : { ...base, title: formData.title };
      await updateDetails({ courseId: course._id, ...payload }).unwrap();
      toast.success(t('editor.saved'));
    } catch (error) {
      toast.error(error.data?.message || t('editor.saveError'));
    }
  };

  const categoryOptions = categories.map((cat) => ({
    value: cat._id,
    label: cat.title || '—',
  }));

  const selectedCategories = categoryOptions.filter((option) =>
    formData.category.includes(option.value)
  );

  const inputCls =
    'w-full px-4 py-2.5 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400';
  const labelCls = 'block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5';

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {/* Left: form */}
        <div className="md:col-span-2 space-y-5">
          <div>
            <label htmlFor="title" className={labelCls}>
              {t('course.title')}
              {isLocked && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 normal-case">
                  <Lock className="w-3 h-3" /> заблокировано
                </span>
              )}
            </label>
            <input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              readOnly={isLocked}
              disabled={isLocked}
              className={`${inputCls} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
              placeholder={t('editor.courseTitlePlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="description" className={labelCls}>{t('course.description')}</label>
            <textarea
              id="description"
              name="description"
              rows={6}
              value={formData.description}
              onChange={handleChange}
              className={`${inputCls} resize-y`}
            />
          </div>

          {/* ── Цена + скидка ───────────────────────────────────
              Раньше тут было ТРИ конфликтующих поля: price, discount%,
              discountPrice. Юзер менял discount=20, но discountPrice оставался
              старым → каталог показывал неактуальную «цену со скидкой».
              Теперь source of truth — только price + discount%, итоговая
              цена вычисляется автоматически и видна юзеру тут же. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="price" className={labelCls}>{t('course.price')} ({t('common.currency')})</label>
              <input
                id="price"
                type="number"
                name="price"
                min="0"
                value={formData.price}
                onChange={handleChange}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="discount" className={labelCls}>{t('course.discount')} (%)</label>
              <input
                id="discount"
                type="number"
                name="discount"
                min="0"
                max="100"
                value={formData.discount}
                onChange={handleChange}
                className={inputCls}
              />
            </div>
          </div>

          {/* Live-preview: что увидит студент в каталоге. Используем тот же
              helper что и CourseCard/CourseDetail — гарантия что preview
              совпадёт с реальным отображением 1-в-1. */}
          {(() => {
            const { basePrice, finalPrice, discountPercent, hasDiscount } = computePricing({
              price: formData.price,
              discount: formData.discount,
            });
            if (basePrice <= 0) return null;
            return (
              <div className="rounded-xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/20 p-3 flex items-center gap-3 text-sm flex-wrap">
                <span className="text-xs text-indigo-700 dark:text-indigo-300 font-medium">
                  Что увидит студент:
                </span>
                {hasDiscount ? (
                  <>
                    <span className="line-through text-slate-400 tabular-nums">
                      {formatKzt(basePrice)}
                    </span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                      {formatKzt(finalPrice)}
                    </span>
                    <span className="text-xs rounded-full bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 px-2 py-0.5 font-semibold">
                      −{discountPercent}%
                    </span>
                  </>
                ) : (
                  <span className="font-bold text-slate-900 dark:text-white tabular-nums">
                    {formatKzt(basePrice)}
                  </span>
                )}
              </div>
            );
          })()}

          <div>
            <label className={labelCls}>{t('course.category')}</label>
            {isLoadingCategories ? (
              <Loading variant="inline" size="sm" />
            ) : (
              <Select
                isMulti
                options={categoryOptions}
                value={selectedCategories}
                onChange={handleCategoryChange}
                placeholder={t('course.category')}
                classNames={selectClasses}
                unstyled
                menuPosition="fixed"
                noOptionsMessage={() => t('course.noCategory')}
              />
            )}
          </div>
        </div>

        {/* Right: cover */}
        <div className="md:col-span-1">
          <label className={labelCls}>
            {t('editor.uploadCover') || 'Обложка курса'}
            {isLocked && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-300 normal-case">
                <Lock className="w-3 h-3" /> заблокировано
              </span>
            )}
          </label>
          <div
            onClick={() => !isUploading && !isLocked && fileInputRef.current?.click()}
            className={`relative aspect-video bg-slate-100 dark:bg-slate-800 rounded-2xl overflow-hidden border-2 border-dashed border-slate-300 dark:border-slate-700 transition ${
              isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-indigo-500 dark:hover:border-indigo-500'
            }`}
          >
            {course.coverImageUrl ? (
              <img
                src={course.coverImageUrl}
                alt={course.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 p-4">
                <ImageIcon className="h-10 w-10 mb-2" />
                <p className="text-sm font-medium">{t('editor.uploadCover') || 'Загрузите обложку'}</p>
                <p className="text-xs mt-1">PNG, JPG, WEBP</p>
              </div>
            )}

            {isUploading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/60 backdrop-blur-sm">
                <Loading variant="button" size="lg" className="text-white" />
                {coverProgress > 0 && (
                  <span className="text-white text-sm font-medium mt-2">{coverProgress}%</span>
                )}
              </div>
            )}
          </div>

          {course.coverImageUrl && !isLocked && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              <UploadCloud size={14} />
              {t('editor.replaceVideo')?.replace('видео', 'обложку') || 'Заменить'}
            </button>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleCoverUpload}
            className="hidden"
            accept="image/*"
          />
        </div>
      </div>

      <div className="border-t dark:border-slate-800 pt-6 flex justify-end">
        <button
          type="submit"
          disabled={isUpdating}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
        >
          {isUpdating ? <Loading variant="button" size="sm" /> : <Save size={16} />}
          {isUpdating ? t('editor.savingAuto') : t('common.save')}
        </button>
      </div>
    </form>
  );
};

export default CourseDetailsForm;
